import { useState, useCallback, useMemo, useEffect } from 'react';
import { QueryGroup, QueryRule, QueryField } from '../../types/QueryBuilder';
import { generateId, isRuleValid } from '../../utils/queryBuilderUtils';
import { useQueryBuilderData } from '../../context/QueryBuilderDataContext';
import { useBrandConfig } from '../../context/BrandConfigContext';
import {
    IDENTIFIER_LIST_FIELD_NAME,
    IDENTIFIERS_DEFAULT_CHECKED_CONFIG_KEY,
    parseConfigIdList,
} from '../../pages/Clients/QueryBuilder/components/SimpleQuery/fieldConfig';

const SIMPLE_GROUP_ID = 'simple-group';

// Fields whose rule should start pre-filled with a given option instead of empty, matching how a
// native <select> (the previous BeOne UI) always has one option selected — never "nothing chosen".
const DEFAULT_OPTION_NAME_BY_FIELD: Record<string, string> = {
    CUSTOMER_ACCOUNT_TYPE: 'point',
};

const resolveDefaultValue = (field: QueryField): string => {
    const defaultOptionName = DEFAULT_OPTION_NAME_BY_FIELD[field.name]?.toLowerCase();
    if (!defaultOptionName) return '';
    // Match on the technical `name` first, but fall back to the human-readable `label` — the API
    // sometimes exposes "point" only as the display label, with an unrelated code as `name`.
    const match = field.values?.find(v => v.name.toLowerCase() === defaultOptionName)
        ?? field.values?.find(v => v.label.toLowerCase() === defaultOptionName);
    return match?.name ?? '';
};

const resolveConfigDefaultIdentifiers = (field: QueryField, configuredIds: string[] | undefined): string[] | undefined => {
    if (field.name !== IDENTIFIER_LIST_FIELD_NAME) return undefined;
    if (!configuredIds?.length || !field.values?.length) return undefined;
    const matched = field.values.filter(v => configuredIds.includes(v.name)).map(v => v.name);
    return matched.length > 0 ? matched : undefined;
};

const createRuleForField = (field: QueryField, configuredIdentifierIds?: string[]): QueryRule => {
    const configDefault = resolveConfigDefaultIdentifiers(field, configuredIdentifierIds);
    if (configDefault) {
        return { id: generateId(), field: field.name, operator: 'in', value: configDefault };
    }
    return {
        id: generateId(),
        field: field.name,
        operator: (field.defaultOperator as QueryRule['operator']) || '=',
        value: resolveDefaultValue(field),
    };
};

export const useSimpleQueryBuilder = () => {
    const { simpleFields, loadingFields, errorFields } = useQueryBuilderData();
    const { getConfigValue } = useBrandConfig();

    const configuredIdentifierIds = useMemo(
        () => parseConfigIdList(getConfigValue(IDENTIFIERS_DEFAULT_CHECKED_CONFIG_KEY)),
        [getConfigValue]
    );

    const [rules, setRules] = useState<QueryRule[]>([]);

    useEffect(() => {
        setRules(prevRules =>
            simpleFields.map(field =>
                prevRules.find(rule => rule.field === field.name) || createRuleForField(field, configuredIdentifierIds)
            )
        );
    }, [simpleFields, configuredIdentifierIds]);

    // The enseigne config can finish loading after the rules were first built (it's fetched
    // separately from the fields), so re-apply the identifiers default once it arrives — but only
    // onto a rule that's still untouched (empty value), never overriding a user's own selection.
    useEffect(() => {
        if (!configuredIdentifierIds?.length) return;
        setRules(prevRules => prevRules.map(rule => {
            if (rule.field !== IDENTIFIER_LIST_FIELD_NAME || rule.value !== '') return rule;
            const field = simpleFields.find(f => f.name === rule.field);
            const configDefault = field && resolveConfigDefaultIdentifiers(field, configuredIdentifierIds);
            return configDefault ? { ...rule, operator: 'in' as const, value: configDefault } : rule;
        }));
    }, [configuredIdentifierIds, simpleFields]);

    const updateField = useCallback((fieldName: string, data: Partial<QueryRule>) => {
        setRules(prevRules =>
            prevRules.map(rule => (rule.field === fieldName ? { ...rule, ...data } : rule))
        );
    }, []);

    const resetFields = useCallback(() => {
        setRules(simpleFields.map(field => createRuleForField(field, configuredIdentifierIds)));
    }, [simpleFields, configuredIdentifierIds]);

    const group = useMemo<QueryGroup>(() => ({
        id: SIMPLE_GROUP_ID,
        combinator: 'AND',
        rules
    }), [rules]);

    const groups = useMemo(() => [group], [group]);

    const isValid = useMemo(() => rules.some(isRuleValid), [rules]);

    return {
        simpleFields,
        rules,
        group,
        groups,
        isValid,
        updateField,
        resetFields,
        loadingFields,
        fieldsError: errorFields
    };
};
